/**
 * Custom React Flow node renderer for a DAQUMA pipeline node.
 *
 * One component renders every node kind; it reads the kind from `data.kind`
 * and looks up its label/category/colour in the node registry. Trigger nodes
 * expose only an output handle; display nodes only an input handle.
 *
 * A running Schedule node gets a live countdown to its next run and a brand-blue
 * ring that sweeps around its edge as that countdown elapses (a conic gradient
 * whose filled arc grows from 0° to 360°).
 */

import { useEffect, useState, type CSSProperties } from "react";
import { Handle, Position, type NodeProps } from "@xyflow/react";
import { Badge, Group, Paper, Stack, Text } from "@mantine/core";
import {
  CATEGORY_COLOR,
  NODE_BY_KIND,
  hasOutput,
  inputPortCount,
  inputPortTitle,
  type NodeCategory,
} from "./nodeRegistry";
import type { ApprovalOut } from "@/shared/api/approvals";
import { ApprovalToggle } from "./ApprovalToggle";

/** Brand palette (src/app/brand.css): navy comet on a blue-grey track — clear
 *  against the #F3F3F3 canvas, where the old yellow all but vanished. */
const WORKING_COMET = "#051F45";
const WORKING = "#56798F";
const WORKING_TRACK = "rgba(86, 121, 143, 0.22)";

type ScheduleState = {
  enabled?: boolean;
  interval_seconds?: number | null;
  next_run_at_utc?: string | null;
};

/** Compact human duration, e.g. "45s", "2m 05s", "1h 03m". */
function formatCountdown(totalSeconds: number): string {
  const s = Math.max(0, totalSeconds);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${String(s % 60).padStart(2, "0")}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${String(m % 60).padStart(2, "0")}m`;
}

export function PipelineNode({ data, selected }: NodeProps) {
  const d = data as Record<string, unknown>;
  const kind = String(d.kind ?? "");
  const def = NODE_BY_KIND.get(kind);
  const label = def?.label ?? kind;
  const category: NodeCategory = def?.category ?? "data-in";
  const color = CATEGORY_COLOR[category];
  const disabled = Boolean(d.disabled);
  // Set at render time by the canvas: not reachable from the trigger.
  const orphan = Boolean(d.orphan);
  const config = (d.config ?? {}) as Record<string, unknown>;
  const inputCount = inputPortCount(kind, config);
  const outputs = def?.outputs ?? [];
  // Set by the last run (see PipelineCanvas). Not part of the saved graph.
  const runStatus = d.runStatus as string | undefined;
  // Set by the canvas while the run waits at this Approval node.
  const approval = d.approval as ApprovalOut | undefined;
  const onApprovalDecided = d.onApprovalDecided as (() => Promise<void> | void) | undefined;

  // Live scheduler state (set by the canvas for a running Schedule node).
  const schedule = d.schedule as ScheduleState | undefined;
  const scheduled =
    kind === "trigger.schedule" &&
    Boolean(schedule?.enabled) &&
    Boolean(schedule?.next_run_at_utc) &&
    Boolean(schedule?.interval_seconds);

  // Live Blob-arrival watch state (set by the canvas for an armed Blob node).
  const blobWatch = d.blobWatch as { configured?: boolean; enabled?: boolean } | undefined;
  const watching = kind === "trigger.blob" && Boolean(blobWatch?.enabled);

  // The engine reports which node it is on, so "working" is now a fact about
  // this node rather than about the run. `d.running` is the fallback for the
  // gap before the first progress write lands — and it stays limited to I/O
  // nodes, since a Constant or a transform is instant and showing it busy would
  // suggest the run was stuck on something that cannot take time.
  // Only the node the engine reports it is on shows as working. The old
  // fallback ("the run is in flight and this source/target has no status yet")
  // lit up every not-yet-reached Load to Veeva at the end of a pipeline.
  const working = runStatus === "running";
  const busy = working;

  // A rotating brand-navy arc for either an armed Blob watch or a node working
  // now (yellow disappeared against the light-grey canvas).
  const sweeping = watching || busy;

  // Tick a local clock only while a schedule is actually counting down.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (!scheduled) return;
    const timer = setInterval(() => setNowMs(Date.now()), 200);
    return () => clearInterval(timer);
  }, [scheduled]);

  // The rotating sweep animation runs while watching or busy.
  const [sweepDeg, setSweepDeg] = useState(0);
  useEffect(() => {
    if (!sweeping) return;
    const timer = setInterval(() => setSweepDeg((deg) => (deg + 30) % 360), 80);
    return () => clearInterval(timer);
  }, [sweeping]);

  let ringStyle: CSSProperties | undefined;
  let countdownLabel: string | null = null;
  if (scheduled && schedule?.next_run_at_utc && schedule?.interval_seconds) {
    const nextMs = new Date(schedule.next_run_at_utc).getTime();
    const intervalMs = schedule.interval_seconds * 1000;
    const remainingMs = Math.max(0, nextMs - nowMs);
    // Fraction of the interval already elapsed → degrees of the ring filled.
    const elapsedFrac = Math.min(1, Math.max(0, 1 - remainingMs / intervalMs));
    const deg = Math.round(elapsedFrac * 360);
    ringStyle = {
      padding: 3,
      borderRadius: 14,
      background: `conic-gradient(${WORKING} ${deg}deg, ${WORKING_TRACK} ${deg}deg)`,
    };
    countdownLabel = `next run in ${formatCountdown(Math.ceil(remainingMs / 1000))}`;
  } else if (sweeping) {
    // A rotating comet: a short bright arc that sweeps quickly around the edge —
    // "watching" (armed Blob) or "working" (this node is running).
    ringStyle = {
      padding: 3,
      borderRadius: 14,
      background: `conic-gradient(from ${sweepDeg}deg, ${WORKING_COMET}, ${WORKING} 40deg, ${WORKING_TRACK} 110deg)`,
      // A soft glow that pulses with the sweep, so a working node is visible
      // even at the edge of the screen.
      boxShadow: `0 0 0 ${2 + Math.round(2 * Math.abs(Math.sin((sweepDeg * Math.PI) / 180)))}px rgba(86, 121, 143, 0.28)`,
    };
  }

  const runBorder =
    runStatus === "running"
      ? WORKING
      : runStatus === "succeeded"
        ? "var(--mantine-color-green-5)"
        : runStatus === "failed"
          ? "var(--mantine-color-red-6)"
          : runStatus === "skipped"
            ? "var(--mantine-color-gray-5)"
            : undefined;

  const card = (
    <Paper
      withBorder
      radius="md"
      p="sm"
      shadow={selected ? "md" : "xs"}
      style={{
        minWidth: 160,
        borderColor: selected
          ? "var(--mantine-primary-color-filled)"
          : orphan
            ? "var(--mantine-color-orange-6)"
            : runBorder,
        borderWidth: runBorder || orphan ? 2 : undefined,
        borderStyle: orphan ? "dashed" : undefined,
        opacity: disabled ? 0.45 : 1,
        minHeight:
          Math.max(inputCount, outputs.length) > 1
            ? Math.max(inputCount, outputs.length) * 26
            : undefined,
      }}
    >
      {Array.from({ length: inputCount }).map((_, i) => (
        <Handle
          key={i}
          type="target"
          id={`in-${i}`}
          title={inputPortTitle(kind, config, i)}
          position={Position.Left}
          style={inputCount > 1 ? { top: `${((i + 1) / (inputCount + 1)) * 100}%` } : undefined}
        />
      ))}
      <Stack gap={4}>
        <Group gap={4} wrap="nowrap">
          <Badge size="xs" color={color} variant="light">
            {category}
          </Badge>
          {disabled && (
            <Badge size="xs" color="gray" variant="outline">
              off
            </Badge>
          )}
          {orphan && (
            <Badge size="xs" color="orange" variant="light">
              not connected
            </Badge>
          )}
          {watching && (
            <Badge size="xs" color="#56798F" variant="light">
              watching
            </Badge>
          )}
          {busy && (
            <Badge size="xs" color="#051F45" variant="filled">
              {working ? "running" : "running…"}
            </Badge>
          )}
        </Group>
        <Text size="sm" fw={600}>
          {label}
        </Text>
        {/* Which pipeline this step runs. Without it a migration is a row of
            identical boxes labelled "Run Pipeline", and the one thing you need
            to read off the canvas — the order the objects load in — is the one
            thing it does not say. */}
        {kind === "flow.run_pipeline" && Boolean(config.pipelineName) && (
          <Text size="xs" c="dimmed" lineClamp={1}>
            {String(config.pipelineName)}
          </Text>
        )}
        {countdownLabel && (
          <Text size="xs" c="#56798F" fw={600}>
            {countdownLabel}
          </Text>
        )}
        {kind === "flow.approval" && Boolean(config.auto_approve) && (
          <Text size="xs" c="green.7" fw={700}>
            Auto-approve on
          </Text>
        )}
        {kind === "flow.approval" && approval && (
          <ApprovalToggle
            approval={approval}
            onDecided={() => {
              void onApprovalDecided?.();
            }}
          />
        )}
      </Stack>
      {hasOutput(category, kind) &&
        (outputs.length > 1 ? (
          // A node that splits its rows shows one labelled handle per port. The
          // labels are load-bearing: two unmarked handles on the same edge of
          // the same box is a coin toss, and wiring the invalid rows into the
          // vault is the one mistake this node exists to prevent.
          outputs.map((port, i) => (
            <Handle
              key={port.id}
              type="source"
              id={port.id}
              position={Position.Right}
              style={{ top: `${((i + 1) / (outputs.length + 1)) * 100}%` }}
            >
              <Text
                size="9px"
                c={port.color ?? "dimmed"}
                fw={600}
                style={{
                  position: "absolute",
                  right: 10,
                  top: -7,
                  pointerEvents: "none",
                  whiteSpace: "nowrap",
                }}
              >
                {port.label}
              </Text>
            </Handle>
          ))
        ) : (
          <Handle type="source" id="out" position={Position.Right} />
        ))}
    </Paper>
  );

  return ringStyle ? <div style={ringStyle}>{card}</div> : card;
}
